import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import { StripeTransaction } from '@cartwhl/common/api/getStripeInvoices';
import DaysAgo from '@cartwhl/ui/atoms/DaysAgo';
import Text from '@cartwhl/ui/base/Text';
import ShareIcon from '@cartwhl/ui/icons/ShareIcon';
import Button from '@cartwhl/ui/input/Button';

import { customerStore, userStore } from 'stores/global';

const INITIAL_LIMIT = 3;
const FETCH_LIMIT = 50;

type TabType = 'Spins' | 'Invoices';

export default function InvoiceAndUsage() {
  const [activeTab, setActiveTab] = useState<TabType>('Invoices');
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const {
    spinTransactions,
    spinTransactionsLoading,
    spinTransactionsNextToken,
    userInfo,
  } = useSnapshot(userStore.state);
  const { customer, invoices, invoicesLoading } = useSnapshot(
    customerStore.state,
  );
  const initialFetchDone = useRef(false);

  useEffect(() => {
    if (userInfo.stripeCustomerID) {
      customerStore.fetchCustomer(userInfo.stripeCustomerID);
    }
  }, [userInfo.stripeCustomerID]);

  useEffect(() => {
    if (activeTab === 'Spins' && !initialFetchDone.current) {
      userStore.fetchSpinTransactions({ limit: INITIAL_LIMIT });
      initialFetchDone.current = true;
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'Invoices' && customer?.id && invoices.length === 0) {
      customerStore.fetchInvoices(customer.id);
    }
  }, [activeTab, customer?.id, invoices.length]);

  function handleViewMore() {
    if (spinTransactionsNextToken) {
      userStore.fetchSpinTransactions({
        limit: FETCH_LIMIT,
        nextToken: spinTransactionsNextToken,
        append: true,
      });
    }
  }

  function renderInvoices() {
    if (invoicesLoading) {
      return (
        <div className="p-6">
          <Text
            as="p"
            variant="studio/body/sm"
            className="text-center opacity-40 dark:opacity-60"
          >
            Loading invoices...
          </Text>
        </div>
      );
    }

    if (invoices.length === 0) {
      return (
        <div className="p-6">
          <Text
            as="p"
            variant="studio/body/medium/sm"
            className="text-gray-45 text-center"
          >
            No invoices found
          </Text>
        </div>
      );
    }

    const displayedInvoices = showAllInvoices
      ? invoices
      : invoices.slice(0, INITIAL_LIMIT);

    return (
      <div>
        <div>
          {displayedInvoices.map(
            (invoice: StripeTransaction, index: number) => (
              <div key={invoice.date}>
                <div className="grid grid-cols-[1fr_2fr_1fr_auto] items-center gap-4 p-4 px-8">
                  <div className="opacity-50 dark:opacity-70">
                    <Text variant="web/body/md" title={invoice.date}>
                      <DaysAgo date={invoice.date} />
                    </Text>
                  </div>
                  <div className="opacity-50 dark:opacity-70">
                    <Text
                      variant="web/body/md"
                      className="text-gray-90 dark:text-white"
                    >
                      {invoice.description}
                    </Text>
                  </div>
                  <div className="opacity-50 dark:opacity-70">
                    <Text variant="web/body/md">{invoice.amountString}</Text>
                  </div>
                  <div className="opacity-100">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<ShareIcon />}
                      onClick={() => {
                        if (invoice.url) {
                          window.open(invoice.url, '_blank');
                        }
                      }}
                    >
                      <p className="hidden sm:block">View details</p>
                    </Button>
                  </div>
                </div>
                {index < displayedInvoices.length - 1 && (
                  <div className="border-gray-30 dark:border-gray-60 mx-4 border-t" />
                )}
              </div>
            ),
          )}
        </div>
        {!showAllInvoices && invoices.length > INITIAL_LIMIT && (
          <div className="flex justify-center p-4">
            <Button
              variant="secondary"
              onClick={() => setShowAllInvoices(true)}
            >
              View all
            </Button>
          </div>
        )}
      </div>
    );
  }

  function renderSpins() {
    if (spinTransactions.length === 0 && !spinTransactionsLoading) {
      return (
        <div className="p-6">
          <Text
            as="p"
            variant="web/body/md"
            className="text-center opacity-40 dark:opacity-60"
          >
            No spin transactions found
          </Text>
        </div>
      );
    }

    return (
      <div>
        <div>
          {spinTransactions.map((transaction, index) => (
            <div key={transaction.SK}>
              <div className="grid grid-cols-[1fr_2fr_1fr] items-center gap-4 p-4 px-8">
                <div>
                  <Text
                    variant="web/body/md"
                    className="opacity-50 dark:opacity-70"
                    title={transaction.createdAt}
                  >
                    <DaysAgo date={transaction.createdAt} />
                  </Text>
                </div>
                <div>
                  <Text
                    variant="web/body/md"
                    className="text-gray-90 dark:text-white"
                  >
                    {transaction.description}
                  </Text>
                </div>
                <div className="text-right">
                  <Text
                    variant="web/body/md"
                    className={
                      transaction.amount > 0
                        ? 'text-[#0A8C27] dark:text-[#4EF272]'
                        : 'text-gray-75 dark:text-gray-45'
                    }
                  >
                    {transaction.amount > 0 ? '+' : ''}
                    {transaction.amount} Spins
                  </Text>
                </div>
              </div>
              {index < spinTransactions.length - 1 && (
                <div className="border-gray-30 dark:border-gray-60 mx-4 border-t" />
              )}
            </div>
          ))}
        </div>
        {spinTransactionsNextToken && (
          <div className="flex justify-center p-4">
            <Button
              variant="secondary"
              onClick={handleViewMore}
              disabled={spinTransactionsLoading}
            >
              {spinTransactionsLoading ? 'Loading...' : 'View more'}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border-gray-30 dark:border-gray-60 rounded-lg border">
      <div className="flex-rows flex flex-nowrap gap-2 p-4">
        <Button
          variant={activeTab === 'Invoices' ? 'secondary' : 'tertiary'}
          onClick={() => setActiveTab('Invoices')}
        >
          Invoices
        </Button>
        <Button
          variant={activeTab === 'Spins' ? 'secondary' : 'tertiary'}
          onClick={() => setActiveTab('Spins')}
        >
          Spins
        </Button>
      </div>
      {activeTab === 'Spins' ? renderSpins() : renderInvoices()}
    </div>
  );
}
